import React, { useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

interface FAQItem {
  question: string;
  answer: string;
}

// Environment variables with fallback values for local development
const companyName = (import.meta.env.VITE_COMPANY_NAME || "").trim() ;
const companyNBFC = (import.meta.env.VITE_COMPANY_NBFC || "").trim() ;
const companyEmail = (import.meta.env.VITE_COMPANY_EMAIL || "").trim() ;

const faqData: FAQItem[] = [
  {
    question: `What is ${companyName}?`,
    answer: `${companyName} is an online platform offering quick and hassle-free personal loans to salaried individuals. As part of ${companyNBFC} (An RBI registered NBFC), we aim to provide accessible financial solutions for emergencies, home improvement, or celebrations.`
  },
  {
    question: "Who is eligible for a personal loan?",
    answer: `To be eligible for a personal loan, you must be:
• A salaried individual
• Aged between 21 and 60 years
• Employed with a recognized organization for at least 6 months
• Earning a minimum monthly salary as per our criteria.`
  },
  {
    question: "What documents are required?",
    answer: `You will need to submit the following documents:
• Proof of identity (Aadhaar, PAN card, passport, etc.)
• Proof of address (utility bills, Aadhaar, etc.)
• Salary slips for the last three months
• Bank statements for the last six months.`
  },
  {
    question: "How much can I borrow?",
    answer: `${companyName} offers personal loans ranging from ₹5,000 to ₹1,00,000 depending on your salary, repayment capacity, and credit score.`
  },
  {
    question: "What is the interest rate?",
    answer: `The interest rate varies based on your credit profile, loan amount, and tenure. Typically, our rates are competitive and designed to be affordable for salaried individuals. Contact us to get a personalized quote.`
  },
  {
    question: "How long does it take to get a loan?",
    answer: `Once you submit your application and documents, our team processes your request quickly. Loan approval usually takes a few minutes, and funds are disbursed within 5 minutes upon approval.`
  },
  {
    question: "Can I repay my loan early?",
    answer: `Yes, ${companyName} allows early repayment of loans. Please review your loan agreement or contact us for details.`
  },
  {
    question: "How is my eligibility assessed?",
    answer: `We assess your loan eligibility based on factors such as your monthly income, credit score, job stability, and repayment history. A good credit score increases your chances of getting approved with a favorable interest rate.`
  },
  {
    question: "Can I apply with a low credit score?",
    answer: `Yes, you can still apply for a loan with a low credit score. However, the approval decision and interest rate may vary based on your overall financial profile and ability to repay the loan.`
  },
  {
    question: "What are the repayment options?",
    answer: `You can repay your loan through various payment options such as NACH (National Automated Clearing House), direct debit, or online payments via our website.`
  },
  {
    question: "Is my data secure?",
    answer: `Absolutely. ${companyName} uses advanced security measures, including encryption and secure servers, to ensure your personal and financial data is protected. We adhere to all data privacy regulations and take confidentiality seriously.`
  },
  {
    question: "Can I track my loan application?",
    answer: `Yes, you can easily track the status of your loan application through our website by logging into your account. You will also receive updates via email and SMS at each stage of the process.`
  },
  {
    question: "How can I contact customer support?",
    answer: `You can reach our customer support team through our website's contact form, by email at ${companyEmail}, or by calling our helpline during business hours. We're here to assist you with any questions or concerns.`
  }
];

const FAQ: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleIndex = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen w-full bg-white text-[var(--text-primary)] relative overflow-x-hidden font-sans selection:bg-[var(--primary)] selection:text-white">
      <div className="relative z-10 flex w-full flex-col items-center">
        <Navbar />

        {/* FAQ Hero Section */}
        <section className="w-full bg-[var(--secondary)] pt-36 pb-16 px-6 lg:px-20 flex flex-col items-center relative overflow-hidden text-center border-b border-[var(--border-light)]">
          {/* Subtle Background Accent */}
          <div className="absolute top-[-10%] left-[-5%] w-[30vw] h-[30vw] bg-[var(--primary)] opacity-[0.02] rounded-full blur-[100px] pointer-events-none" />

          <div className="max-w-5xl mx-auto relative z-10 flex flex-col items-center gap-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[var(--primary)]/30 bg-white/60 shadow-sm text-[10px] font-black tracking-widest text-[var(--primary)] uppercase leading-none">
              <span className="w-2.5 h-2.5 rounded-full bg-[var(--primary)] animate-pulse" />
              <span>FAQ & Support</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[54px] font-black text-[#0F172A] leading-tight tracking-tight mt-4 leading-none">
              Common <span className="text-[var(--primary)]">Questions</span>
            </h1>

            <p className="text-slate-500 font-medium text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              Find the answers to your salary advance and financial wellness questions below. We're here to help you every step of the way.
            </p>
          </div>
        </section>

        {/* FAQ Accordion List Section */}
        <section className="w-full py-16 sm:py-20 px-6 lg:px-20 bg-white relative z-10 flex flex-col items-center">
          <div className="max-w-[760px] w-full flex flex-col">
            {faqData.map((item, idx) => {
              const isOpen = activeIndex === idx;
              return (
                <div 
                  key={idx}
                  className="border-b border-slate-100 py-5 sm:py-6 transition-all duration-300"
                >
                  <button
                    onClick={() => toggleIndex(idx)}
                    className="w-full flex justify-between items-center text-left group"
                  >
                    <span className={`font-black text-[16px] sm:text-[18px] tracking-tight transition-colors duration-300 pr-6 ${
                      isOpen ? 'text-[var(--primary)]' : 'text-[#0F172A]'
                    }`}>
                      {item.question}
                    </span>
                    <div className={`shrink-0 text-slate-400 group-hover:text-[#0F172A] transition-colors duration-300`}>
                      {isOpen ? (
                        <FaChevronUp className="text-[13px] sm:text-[14px]" />
                      ) : (
                        <FaChevronDown className="text-[13px] sm:text-[14px]" />
                      )}
                    </div>
                  </button>

                  {/* Accordion panel with smooth slide transition */}
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100 mt-3 sm:mt-4' : 'grid-rows-[0fr] opacity-0 pointer-events-none'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-slate-500 font-medium text-[14px] sm:text-[15px] leading-relaxed whitespace-pre-line">
                        {item.answer}
                      </p>
                    </div>
                  </div>

                </div>
              );
            })}

            {/* Bottom Still Have Questions Banner Card */}
            <div className="bg-[#0B1220] rounded-[24px] p-6 sm:p-8 md:p-10 border border-white/[0.04] shadow-[0_10px_25px_rgba(11,30,63,0.15)] relative overflow-hidden flex flex-col md:flex-row md:items-center justify-between gap-6 mt-12 w-full text-left">
              {/* Watermark symbol in bottom-right */}
              <div className="absolute right-[5px] bottom-[-25px] text-white/[0.03] text-[150px] font-black pointer-events-none select-none rotate-[8deg] z-0 leading-none">
                ?
              </div>

              <div className="relative z-10 flex flex-col gap-2">
                <h3 className="text-[var(--primary)] font-black text-lg sm:text-[20px] tracking-tight">
                  Still have questions?
                </h3>
                <p className="text-slate-400 font-medium text-[13px] sm:text-[14px] leading-relaxed">
                  Our team of experts is available around the clock to help you.
                </p>
              </div>

              <div className="relative z-10 flex-shrink-0">
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-[#0F172A] font-extrabold text-[12px] uppercase tracking-wider py-3.5 px-6 rounded-xl transition-all active:scale-95 shadow-sm"
                >
                  <span>Contact Support</span>
                  <svg className="w-4 h-4 text-[#0B1220]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 1v4M3 10a9 9 0 0118 0M3 10h2a2 2 0 012 2v3a2 2 0 01-2 2H3m18-7h-2a2 2 0 00-2 2v3a2 2 0 002 2h2m-2-12a2 2 0 00-2 2" />
                  </svg>
                </a>
              </div>
            </div>

          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default FAQ;
